/**
 * SaveButton — reusable icon-only "save" toggle for index cards.
 *
 * Used across every index page (events, articles, regulations, organizations).
 * Renders a square outlined icon button pinned to the top-right corner of a
 * relatively-positioned card; it fills with `color` once the item is saved.
 * Saved state is derived from auth context (see `useIsSaved`) so the styling
 * stays in sync without any per-page wiring.
 *
 * Pass the accent `color` plus everything needed to identify/save the item
 * (`resourceType`, `resourceId`, `resourceSlug`, and the optional title /
 * summary / image used for the saved-item preview).
 */
import React from "react";
import { Box, alpha } from "@mui/material";
import SavedToggleButton from "@/components/Saved/SavedToggleButton";
import { useIsSaved } from "@/hooks/useIsSaved";
import { jsx as _jsx } from "react/jsx-runtime";
export const SaveButton = ({
  resourceType,
  resourceId,
  resourceSlug,
  resourceTitle,
  resourceSummary,
  resourceImage,
  color,
  positionSx
}) => {
  const isSaved = useIsSaved(resourceType, resourceId, resourceSlug);
  return /*#__PURE__*/_jsx(Box
  // Inset a few px from the corner (not overhanging, not flush at 0).
  // The host Card has `overflow: hidden` and a ~4px rounded corner, so
  // a flush button gets its square top-right corner clipped into a
  // curve. A small inset clears that corner radius so the button reads
  // as a clean square, while staying inside the border so its top/
  // right edges are never clipped. High z-index keeps it above the
  // card border in every state.
  , {
    sx: {
      position: "absolute",
      top: 3,
      right: 3,
      zIndex: 3,
      ...positionSx
    },
    onClick: e => e.stopPropagation(),
    children: /*#__PURE__*/_jsx(SavedToggleButton, {
      resourceType: resourceType,
      resourceId: resourceId !== null && resourceId !== undefined ? String(resourceId) : resourceSlug ?? null,
      resourceSlug: resourceSlug,
      resourceTitle: resourceTitle,
      resourceSummary: resourceSummary,
      resourceImage: resourceImage,
      "aria-label": resourceTitle ? `Save ${resourceTitle}` : `Save ${resourceType}`,
      label: {
        save: "",
        saved: ""
      },
      confirmSave: true,
      size: "medium",
      variant: "text",
      sx: {
        minWidth: 0,
        // Even 6px padding on all sides — the icon needs horizontal
        // breathing room, not the squished sliver it had.
        p: 0.75,
        borderRadius: 0,
        border: `2px solid ${color}`,
        bgcolor: isSaved ? color : "white",
        color: isSaved ? "white" : color,
        "&:hover": {
          bgcolor: isSaved ? color : alpha(color, 0.08)
        },
        "& .MuiButton-startIcon": {
          margin: 0
        },
        "& .MuiSvgIcon-root": {
          fontSize: "1.5rem"
        }
      }
    })
  });
};
export default SaveButton;